// Libs
import classNames from 'classnames/bind';
import { Box, Checkbox, FormControlLabel, FormGroup, InputLabel, Stack, Typography } from '@mui/material';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { pdf } from '@react-pdf/renderer';
// Components, Layouts, Pages
import {
  BaseAvatarGroup,
  BaseDatePicker,
  BaseFilters,
  BaseInputSearch,
  BaseMoreAction,
  BaseSelect,
  BaseStatus,
  BaseTable,
  BaseTableUser,
  BaseToolbar,
  ChangeValueModal,
  ConfirmModal,
  Loading,
  UnderDevelopment,
} from '~/components/common';
import { SendPdfTemplate } from '~/components/module/estimate';
// Others
import useDebounce from '~/utils/hooks/useDebounce';
import { StorageEnum } from '~/utils/enum/base';
import { DateFormatEnum, RoleEnum, SearchByFilterContentEnum } from '~/utils/enum/common';
import {
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_ESTIMATE_FILE_NAME_PDF,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_NUMBER_ONE,
  DEFAULT_NUMBER_ZERO,
  DEFAULT_VALUE_FILTER,
  EMPTY_STRING,
  FILTER_DEFAULT_VALUES,
  POUND,
  SEARCH_BY_KEY,
} from '~/utils/constants/common';
import { dataTimeZone, formatCurrency, formatDate, formatTitleCase } from '~/utils/helper/common';
import {
  IBaseMoreAction,
  IFilterValueChange,
  IHeaderCellType,
  IQueryParamBase,
} from '~/utils/interface/common';
import { IPaginationResponse } from '~/utils/interface/base';
import {
  IChangeStatusEstimate,
  IEstimate,
  IEstimateDetailUpdate,
  IParamGetListEstimate,
  IPayloadChangeStatusEstimate,
  IPayloadDetailUpdateEstimate,
  IPayloadSendEstimate,
} from '~/utils/interface/estimate';
import { adminRouteAbsolute } from '~/utils/routes/admin';
import { staffRouteAbsolute } from '~/utils/routes/staff';
import { useAppDispatch } from '~/utils/redux/hooks';
import {
  changeStatusEstimate,
  convertToJob,
  deleteEstimate,
  getEstimateDetailUpdate,
  getListEstimate,
  sendEstimate,
} from '~/thunks/estimate/estimateThunk';
import { estimateActions, selectIsRefreshEstimateList } from '~/thunks/estimate/estimateSlice';
import { mockDataChangeStatusEstimate, mockDataEstimateStatus } from '~/utils/mockData/estimate';
import { schema } from './helper';
// Styles, images, icons
import styles from './CustomerEstimate.module.scss';
import { icons } from '~/assets';

const cx = classNames.bind(styles);

const columns = (
  role: string,
  t: TFunction,
  onView: (id: string) => void,
  onEdit: (id: string) => void,
  onDelete: (id: string) => void,
  onConvertToJob: (id: string) => void,
  onNavigateJob: (jobId: string) => void,
  onNavigateEstimate: (estimateId: string) => void,
  onSendEstimate: (id: string) => void,
  onChangeStatus: (record: IEstimate) => void
): IHeaderCellType<IEstimate>[] => {
  return [
    {
      id: 'id',
      label: t('table_head_detail_customer_title_estimate_id'),
      render: (record: IEstimate) => {
        return record?.id ? (
          <Box
            component={'div'}
            className={cx('jobEstimate')}
            onClick={(e) => {
              e.stopPropagation();
              onNavigateEstimate(record?.id);
            }}
          >
            {POUND}
            {record?.id}
          </Box>
        ) : (
          <Box component={'div'}>{EMPTY_STRING}</Box>
        );
      },
    },
    {
      id: 'jobId',
      label: t('table_head_detail_customer_title_job_id'),
      render: (record: IEstimate) => {
        return record?.job?.id ? (
          <Box
            component={'div'}
            className={cx('jobEstimate')}
            onClick={(e) => {
              e.stopPropagation();
              onNavigateJob(record?.job?.id);
            }}
          >
            {POUND}
            {record?.job?.id}
          </Box>
        ) : (
          <Box component={'div'}>{EMPTY_STRING}</Box>
        );
      },
    },
    {
      id: 'assignees',
      label: t('table_head_detail_customer_title_assignee'),
      minWidth: 120,
      render: (record: IEstimate) => {
        return record?.assignees && record?.assignees.length === DEFAULT_NUMBER_ONE ? (
          <BaseTableUser
            user={{
              avatar: record.assignees[DEFAULT_NUMBER_ZERO]?.avatarImage,
              firstName: record.assignees[DEFAULT_NUMBER_ZERO]?.firstName,
              lastName: record.assignees[DEFAULT_NUMBER_ZERO]?.lastName,
            }}
          />
        ) : (
          <BaseAvatarGroup avatars={record?.assignees || []} borderColor='#FFF' max={5} />
        );
      },
    },
    {
      id: 'createdAt',
      label: t('table_head_detail_customer_title_created_date'),
      minWidth: 100,
      render: (record: IEstimate) => {
        return record?.createdAt ? formatDate(record.createdAt, DateFormatEnum.DD_MMM_YYYY) : EMPTY_STRING;
      },
    },
    {
      id: 'total',
      label: t('table_head_detail_customer_title_amount'),
      minWidth: 100,
      render: (record: IEstimate) => {
        return formatCurrency(record?.total) || EMPTY_STRING;
      },
    },
    {
      id: 'status',
      label: t('table_head_detail_customer_title_status'),
      minWidth: 100,
      render: (record: IEstimate) => {
        return record.status ? <BaseStatus label={record.status} /> : EMPTY_STRING;
      },
    },
    {
      id: 'action',
      label: t('table_head_detail_customer_title_action'),
      align: 'left',
      render: (record: IEstimate) => {
        const actions: IBaseMoreAction[] = [
          {
            icon: (
              <img
                src={icons.tableIconActionView}
                alt={t('common_alt_img', { ns: 'common/base' })}
                className={cx('itemIconImg')}
              />
            ),
            label: t('table_action_view', { ns: 'common/base' }),
            onClick: () => onView(record.id),
          },
          {
            icon: (
              <img
                src={icons.tableIconActionEdit}
                alt={t('common_alt_img', { ns: 'common/base' })}
                className={cx('itemIconImg')}
              />
            ),
            label: t('table_action_edit', { ns: 'common/base' }),
            onClick: () => onEdit(record.id),
          },
          {
            icon: (
              <img
                src={icons.tableIconActionSendEstimate}
                alt={t('common_alt_img', { ns: 'common/base' })}
                className={cx('itemIconImg')}
              />
            ),
            label: t('table_action_send_estimate', { ns: 'common/base' }),
            onClick: () => onSendEstimate(record.id),
          },
        ];

        if (role === RoleEnum.ADMIN) {
          actions.push({
            icon: (
              <img
                src={icons.tableIconActionChangeStatus}
                alt={t('common_alt_img', { ns: 'common/base' })}
                className={cx('itemIconImg')}
              />
            ),
            label: t('table_action_change_status', { ns: 'common/base' }),
            onClick: () => onChangeStatus(record),
          });
        }

        if (!record?.job?.id) {
          actions.push({
            icon: (
              <img
                src={icons.tableIconActionChangeStatus}
                alt={t('common_alt_img', { ns: 'common/base' })}
                className={cx('itemIconImg')}
              />
            ),
            label: t('table_action_convert_to_job', { ns: 'common/base' }),
            onClick: () => onConvertToJob(record.id),
          });
        }

        actions.push({
          icon: (
            <img
              src={icons.tableIconActionDelete}
              alt={t('common_alt_img', { ns: 'common/base' })}
              className={cx('itemIconImg')}
            />
          ),
          label: t('table_action_delete', { ns: 'common/base' }),
          onClick: () => onDelete(record.id),
          hasOtherColor: true,
        });

        return <BaseMoreAction actions={actions} />;
      },
    },
  ];
};

const CustomerEstimate = () => {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/estimate']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(
    () => Number(params?.page ?? BASE_PAGINATION_DEFAULT_CURRENT_PAGE),
    [params?.page]
  );
  const limitPage = useMemo<number>(() => Number(params?.limit ?? DEFAULT_LIMIT_PAGE), [params?.limit]);
  const textSearchParams = useMemo<string>(
    () => String(params?.textSearch || EMPTY_STRING),
    [params?.textSearch]
  );
  const { id: customerId } = useParams();
  const currentRole: string = localStorage.getItem(StorageEnum.ROLE) ?? EMPTY_STRING;
  //#endregion Declare Hook

  //#region Selector
  const isRefreshListEstimate = useSelector(selectIsRefreshEstimateList);
  //#endregion Selector

  //#region Declare State
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  const [filterParams, setFilterParams] = useState<IParamGetListEstimate>(FILTER_DEFAULT_VALUES);
  const [searchPlaceholder, setSearchPlaceholder] = useState<string>(EMPTY_STRING);

  const [paramObject, setParamObject] = useState<IParamGetListEstimate>({
    page: pageSelected,
    limit: limitPage,
    clientId: customerId,
  });
  const [searchKey, setSearchKey] = useState<string>(EMPTY_STRING);
  const debounceSearchKey = useDebounce(searchKey, DEFAULT_DEBOUNCE_DELAY_TIME);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [pagination, setPagination] = useState<IPaginationResponse>();
  const [listEstimate, setListEstimate] = useState<IEstimate[]>([]);

  const [estimateSelectedId, setEstimateSelectedId] = useState<string>(EMPTY_STRING);
  const [isShowModalConvertToJob, setIsShowModalConvertToJob] = useState<boolean>(false);
  const [isShowConfirmDelete, setIsShowConfirmDelete] = useState<boolean>(false);
  const [isShowChangeStatus, setIsShowChangeStatus] = useState<boolean>(false);
  const [estimateSelected, setEstimateSelected] = useState<IEstimate | null>(null);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!customerId) return;
    const { status, ...restParams } = paramObject;

    const newParamObject: IParamGetListEstimate = {
      ...restParams,
      clientId: customerId,
      ...(status === DEFAULT_VALUE_FILTER ? {} : { status }),
    };

    handleGetListEstimate(newParamObject);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);

  useEffect(() => {
    if (!isRefreshListEstimate) return;

    handleGetListEstimate(paramObject);
    dispatch(estimateActions.setRefreshEstimateList(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshListEstimate]);

  useEffect(() => {
    setSearchKey(textSearchParams);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textSearchParams]);

  useEffect(() => {
    if (pagination || debounceSearchKey) {
      setParamObject({
        ...paramObject,
        page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
        limit: limitPage,
        ...(debounceSearchKey ? { textSearch: debounceSearchKey } : { textSearch: undefined }),
        ...(filterParams ? { ...filterParams } : {}),
      });

      setSearchParams({
        page: String(BASE_PAGINATION_DEFAULT_CURRENT_PAGE),
        limit: String(limitPage),
        ...(searchKey ? { textSearch: debounceSearchKey } : {}),
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearchKey]);

  useEffect(() => {
    if (!searchKey) return;

    setParamObject({
      ...paramObject,
      page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
      limit: limitPage,
      ...(filterParams ? { ...filterParams } : {}),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterParams]);

  useEffect(() => {
    if (Array(filterParams.fields)) {
      const placeholder = filterParams.fields?.map((item) => formatTitleCase(item))?.join(', ');
      setSearchPlaceholder(placeholder as string);
    }
  }, [filterParams.fields]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleEdit = (id: string) => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(`${adminRouteAbsolute.updateEstimate(id)}`);
        break;

      case RoleEnum.STAFF:
        navigate(`${staffRouteAbsolute.updateEstimate(id)}`);
        break;
    }
  };

  const handleDelete = (id: string) => {
    setIsShowConfirmDelete(true);
    setEstimateSelectedId(id);
  };

  const handleView = (id: string) => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(`${adminRouteAbsolute.estimate}/${id}`);
        break;

      case RoleEnum.STAFF:
        navigate(`${staffRouteAbsolute.estimate}/${id}`);
        break;
    }
  };

  const handleRowTableClick = (id: string) => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(`${adminRouteAbsolute.estimate}/${id}`);
        break;

      case RoleEnum.STAFF:
        navigate(`${staffRouteAbsolute.estimate}/${id}`);
        break;
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchKey(value);
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
    setSearchParams({ ...params, page: String(page), limit: String(limitPage) });
  };

  const handleRowPerPageChange = (rowPerPage: number) => {
    setParamObject({ ...paramObject, page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE, limit: rowPerPage });
    setSearchParams({
      ...params,
      page: String(BASE_PAGINATION_DEFAULT_CURRENT_PAGE),
      limit: String(rowPerPage),
    });
  };

  const handleNavigateToFormEstimate = () => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(`${adminRouteAbsolute.createEstimate}?customerId=${customerId}`);
        break;

      case RoleEnum.STAFF:
        navigate(`${staffRouteAbsolute.createEstimate}?customerId=${customerId}`);
        break;
    }
  };

  const handleCloseUnderDevelopment = () => {
    setIsShowUnderDevelopment(false);
  };

  const handleGetListEstimate = (params: IParamGetListEstimate) => {
    setIsLoading(true);

    dispatch(getListEstimate(params))
      .unwrap()
      .then((resp) => {
        const { responses, pagination } = resp.data;
        if (!responses) return;

        setListEstimate(responses);
        setPagination(pagination);
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleConvertToJob = (id: string) => {
    setEstimateSelectedId(id);
    setIsShowModalConvertToJob(true);
  };

  const handleCloseModalConvertToJob = () => {
    setEstimateSelectedId(EMPTY_STRING);
    setIsShowModalConvertToJob(false);
  };

  const handleConfirmConvertToJob = () => {
    if (!estimateSelectedId) return;

    setIsLoading(true);

    dispatch(convertToJob(estimateSelectedId))
      .unwrap()
      .then(() => {
        dispatch(estimateActions.setRefreshEstimateList(true));
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
        handleCloseModalConvertToJob();
      });
  };

  const handleNavigateJobDetail = (jobId: string) => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(`${adminRouteAbsolute.jobs}/${jobId}`);
        break;

      case RoleEnum.STAFF:
        navigate(`${staffRouteAbsolute.jobs}/${jobId}`);
        break;
    }
  };

  const handleNavigateEstimateDetail = (estimateId: string) => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(`${adminRouteAbsolute.estimate}/${estimateId}`);
        break;

      case RoleEnum.STAFF:
        navigate(`${staffRouteAbsolute.estimate}/${estimateId}`);
        break;
    }
  };

  const handleCloseConfirmDelete = () => {
    setIsShowConfirmDelete(false);
    setEstimateSelectedId(EMPTY_STRING);
  };

  const handleConfirmDeleteEstimate = () => {
    if (!estimateSelectedId) return;

    setIsLoading(true);

    dispatch(deleteEstimate(estimateSelectedId))
      .unwrap()
      .then((res) => {
        dispatch(estimateActions.setRefreshEstimateList(true));
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
        handleCloseConfirmDelete();
      });
  };

  const handleSendEstimate = async (id: string) => {
    try {
      setIsLoading(true);
      const payload: IPayloadDetailUpdateEstimate = {
        estimateId: id,
        params: {
          timeZone: dataTimeZone(),
        },
      };
      const { data } = await dispatch(getEstimateDetailUpdate(payload)).unwrap();

      const pdfBlob = await generatePdfBlob(data);
      if (pdfBlob) {
        const formData = new FormData();
        formData.append('file', pdfBlob, DEFAULT_ESTIMATE_FILE_NAME_PDF);

        const payload: IPayloadSendEstimate = {
          estimateId: id,
          body: formData,
        };
        await dispatch(sendEstimate(payload))
          .unwrap()
          .then((res) => {
            dispatch(estimateActions.setRefreshEstimateList(true));
          })
          .catch((err) => {})
          .finally(() => setIsLoading(false));
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const generatePdfBlob = async (dataPdf: IEstimateDetailUpdate): Promise<Blob | null> => {
    try {
      return await pdf(<SendPdfTemplate data={dataPdf} />).toBlob();
    } catch (error) {
      return null;
    }
  };

  const handleChangeStatus = (record: IEstimate) => {
    setIsShowChangeStatus(true);
    setEstimateSelected(record);
  };

  const handleCloseChangeStatus = () => {
    setIsShowChangeStatus(false);
    setEstimateSelected(null);
  };

  const handleSubmitChangeStatus = async (data: IChangeStatusEstimate) => {
    if (!estimateSelected?.id) return;
    const payload: IPayloadChangeStatusEstimate = {
      estimateId: estimateSelected?.id,
      body: data,
    };
    await dispatch(changeStatusEstimate(payload));
  };

  const handleChangeDate = (
    date: string,
    onChange?: (value: IFilterValueChange<IParamGetListEstimate>) => void
  ) => {
    onChange?.({ name: 'createDate', value: date });
  };

  const onFilterChildrenChange = (
    event: ChangeEvent<HTMLInputElement>,
    valueFilter: IQueryParamBase | undefined,
    onChange?: (value: IFilterValueChange<IQueryParamBase>) => void
  ) => {
    const { name, value, checked } = event.target;
    const newFields: IQueryParamBase = {
      ...filterParams,
      fields: checked
        ? [...(valueFilter?.fields || []), value]
        : (valueFilter?.fields || [])?.filter((item) => item !== value),
    };

    if (name === SEARCH_BY_KEY && onChange) {
      onChange({ name: 'fields', value: newFields?.fields });
    }
  };

  const handleApplyFilter = (values: IParamGetListEstimate | undefined) => {
    if (!values) {
      setFilterParams({});
      return;
    }

    setFilterParams({
      ...values,
    });

    if (values?.status || values?.createDate) {
      setParamObject({
        ...paramObject,
        ...(values?.fields?.length ? { fields: values.fields } : { fields: undefined }),
        ...(values?.status ? { status: values?.status } : {}),
        ...(values?.createDate ? { createDate: values?.createDate } : {}),
      });
    }
  };
  //#endregion Handle Function

  return (
    <Box component={'div'} className={cx('container')} id='customerEstimatePage'>
      <BaseToolbar textAction={t('button_add_estimate')} onAction={handleNavigateToFormEstimate}>
        <BaseInputSearch
          searchByContent={
            searchPlaceholder
              ? `${SearchByFilterContentEnum.ID}, ${searchPlaceholder}`
              : SearchByFilterContentEnum.ID
          }
          value={searchKey}
          onChange={handleSearchChange}
        />

        <BaseFilters<IParamGetListEstimate>
          value={filterParams}
          defaultValue={FILTER_DEFAULT_VALUES}
          onApply={handleApplyFilter}
        >
          {({ value, onChange }) => (
            <Stack gap={'12px'}>
              <Typography className={cx('title')}>
                {t('common_action_search_by', { ns: 'common/base' })}
              </Typography>

              <FormGroup>
                <FormControlLabel
                  className={cx('formControlLabel')}
                  slotProps={{
                    typography: {
                      className: cx('checkboxLabel'),
                    },
                  }}
                  control={
                    <Checkbox
                      name={SEARCH_BY_KEY}
                      className={cx('checkbox')}
                      value={'JOB_ID'}
                      checked={value?.fields?.includes('JOB_ID') || false}
                      onChange={(event) => onFilterChildrenChange(event, value, onChange)}
                    />
                  }
                  label={t('search_label_job_id')}
                />

                <FormControlLabel
                  className={cx('formControlLabel')}
                  slotProps={{
                    typography: {
                      className: cx('checkboxLabel'),
                    },
                  }}
                  control={
                    <Checkbox
                      name={SEARCH_BY_KEY}
                      className={cx('checkbox')}
                      value={'ASSIGNEES'}
                      checked={value?.fields?.includes('ASSIGNEES') || false}
                      onChange={(event) => onFilterChildrenChange(event, value, onChange)}
                    />
                  }
                  label={t('search_label_assignee')}
                />
              </FormGroup>

              <Typography className={cx('title')}>
                {t('common_action_filter_by', { ns: 'common/base' })}
              </Typography>

              <Box component={'div'} className={cx('filterGroup')}>
                <InputLabel className={cx('label')}>{t('filter_label_status')}</InputLabel>

                <BaseSelect
                  layout='horizontal'
                  width={'135px'}
                  options={mockDataEstimateStatus}
                  value={value?.status || DEFAULT_VALUE_FILTER}
                  onChange={(optionSelected) => {
                    onChange &&
                      onChange({
                        name: 'status',
                        value: optionSelected?.value.toString(),
                      });
                  }}
                />
              </Box>

              <Box component={'div'} className={cx('filterGroup')}>
                <InputLabel className={cx('label')}>{t('filter_label_created_date')}</InputLabel>

                <BaseDatePicker
                  value={value?.createDate}
                  onChange={(value) => handleChangeDate(value, onChange)}
                  width={'135px'}
                />
              </Box>
            </Stack>
          )}
        </BaseFilters>
      </BaseToolbar>

      <Box component={'div'} className={cx('body')}>
        <BaseTable
          currentRowsPerPage={limitPage}
          currentPage={pageSelected}
          count={pagination?.totalPages || DEFAULT_NUMBER_ZERO}
          columns={columns(
            currentRole,
            t,
            handleView,
            handleEdit,
            handleDelete,
            handleConvertToJob,
            handleNavigateJobDetail,
            handleNavigateEstimateDetail,
            handleSendEstimate,
            handleChangeStatus
          )}
          dataSource={listEstimate || []}
          onPageChange={handlePageChange}
          onRowClick={handleRowTableClick}
          onRowsPerPageChange={handleRowPerPageChange}
        />
      </Box>

      {isLoading && <Loading />}

      {isShowModalConvertToJob && (
        <ConfirmModal
          type='default'
          icon={icons.tableIconActionChangeStatus}
          title={t('common_title_confirm_convert_to_job', { ns: 'common/base' })}
          textConfirm={t('common_convert_action', { ns: 'common/base' })}
          onClose={handleCloseModalConvertToJob}
          onConfirm={handleConfirmConvertToJob}
        />
      )}

      {isShowConfirmDelete && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_delete', { ns: 'common/base' })}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseConfirmDelete}
          onConfirm={handleConfirmDeleteEstimate}
        />
      )}

      {isShowChangeStatus && (
        <ChangeValueModal
          title={t('form_change_status_title')}
          defaultValues={{
            status: estimateSelected?.status || EMPTY_STRING,
          }}
          onSubmit={handleSubmitChangeStatus}
          onClose={handleCloseChangeStatus}
          schema={schema(t)}
          fields={[
            {
              name: 'status',
              label: t('form_change_status_label_status'),
              options: mockDataChangeStatusEstimate,
              required: true,
            },
          ]}
        />
      )}

      <UnderDevelopment isOpen={isShowUnderDevelopment} onClose={handleCloseUnderDevelopment} />
    </Box>
  );
};

export default CustomerEstimate;
